/**
 * <keystone-field-enum> — single-select dropdown.
 *
 * Maps onto JSON Schema string with `enum: [...]`. Options come in as a
 * property (not an attribute) so we can pass non-string-friendly data:
 *
 *   const el = document.querySelector("keystone-field-enum");
 *   el.options = ["draft", "approved", "rejected"];
 *
 * The form composite (<keystone-form>) builds this from `schema.enum`.
 * A placeholder row with empty value is added when `required` is false
 * so users can clear the field; required selects start with a "Select…"
 * sentinel that's disabled so the form fails submit if untouched.
 */

import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fieldStyles, humaniseFieldName } from "./base.js";

@customElement("keystone-field-enum")
export class KeystoneFieldEnum extends LitElement {
  static override styles = fieldStyles;

  @property({ type: String }) name = "";
  @property({ type: String }) label = "";
  @property({ type: String }) value = "";
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error: string | null = null;
  @property({ type: Boolean }) disabled = false;

  /** Enum values. Pass as a property (not an attribute). */
  @property({ type: Array }) options: string[] = [];

  override updated(): void {
    if (this.error) this.setAttribute("data-error", "");
    else this.removeAttribute("data-error");
  }

  private onChange(e: Event): void {
    const select = e.target as HTMLSelectElement;
    this.value = select.value;
    this.dispatchEvent(
      new CustomEvent("keystone-field-input", {
        detail: { name: this.name, value: select.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const label = this.label || humaniseFieldName(this.name);
    const inputId = `input-${this.name}`;

    return html`
      <label for=${inputId}>
        ${label}${this.required
          ? html`<span class="required-marker" aria-hidden="true">*</span>`
          : ""}
      </label>
      <select
        id=${inputId}
        name=${this.name}
        ?required=${this.required}
        ?disabled=${this.disabled}
        aria-invalid=${this.error ? "true" : "false"}
        aria-describedby=${this.error ? `${inputId}-error` : ""}
        @change=${this.onChange}
      >
        ${this.required && !this.value
          ? html`<option value="" disabled selected>
              Select…
            </option>`
          : html`<option value="" ?selected=${this.value === ""}>
              ${this.required ? "Select…" : "— none —"}
            </option>`}
        ${this.options.map(
          (opt) =>
            html`<option value=${opt} ?selected=${opt === this.value}>
              ${humaniseFieldName(opt)}
            </option>`,
        )}
      </select>
      ${this.error
        ? html`<p class="error" id="${inputId}-error" role="alert">
            ${this.error}
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-field-enum": KeystoneFieldEnum;
  }
}
